/**
 * RecurringScreen — 固定週期 list (`/recur`).
 *
 * Pixel-faithful port of the `RecurringScreen` function in
 * `design/project/screens-features.jsx` (re-exported via `screens-misc`).
 *
 * Layout:
 *   1. Title + summary cards (月度支出 / 月度收入).
 *   2. "進行中" group (active definitions).
 *   3. "已暫停" group (active=false definitions).
 *   4. RecurringEditSheet opens on tap of any row or the `+` action.
 *
 * Reads from `useStore(s => s.recurring.items)`. Toggling active emits
 * `update_recurring` enqueue; create/edit emits `create_recurring` /
 * `update_recurring`. Optimistic updates via `recurring.addDef` /
 * `updateDef`.
 */
import { useMemo, useState } from 'react';

import Icon, { type IconName } from '@/components/common/Icon';
import { displayTxIcon } from '@/lib/displayTxIcon';
import { formatAmount } from '@/lib/format/currency';
import { useStore } from '@/state/store';
import { enqueueOp } from '@/lib/sheets/syncWiring';
import { kickScheduler } from '@/lib/recurring/runScheduler';
import type { RecurringRow } from '@/lib/sheets/types';
import RecurringEditSheet, {
  type RecurringFormPatch,
} from './RecurringEditSheet';

export interface RecurringScreenProps {
  currency?: string;
  /** When set, render a back chevron in the header that calls this. Used
   *  on mobile when this screen is reached from /settings → 固定週期 so
   *  the user can return to the settings list. */
  onBack?: () => void;
}

const FREQ_LABELS: Record<RecurringRow['freq'], (day: number) => string> = {
  daily: () => '每日',
  weekly: (d) => `每週${'日一二三四五六'[d] ?? ''}`,
  monthly: (d) => `每月 ${d} 號`,
  yearly: (d) => `每年第 ${d} 天`,
};

function genId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function RecurringScreen({
  currency = 'TWD',
  onBack,
}: RecurringScreenProps): JSX.Element {
  const items = useStore((s) => s.recurring.items);
  const categories = useStore((s) => s.categories.items);
  const activeBookId = useStore((s) => s.books.activeBookId);
  const addDef = useStore((s) => s.recurring.addDef);
  const updateDef = useStore((s) => s.recurring.updateDef);

  // Scope category options to the active book — system categories
  // (`book_id=''`) are shared across all books, but user-created categories
  // belonging to a different, non-active book must not leak into the
  // recurring editor's picker. Mirrors the filter already applied in
  // `App.tsx` for the settings/add-tx category pickers and OCR review.
  const visibleCategories = useMemo(
    () =>
      categories.filter(
        (c) => c.book_id === '' || (activeBookId != null && c.book_id === activeBookId),
      ),
    [categories, activeBookId],
  );

  const [editing, setEditing] = useState<RecurringRow | null>(null);
  const [showNew, setShowNew] = useState(false);

  const { active, paused } = useMemo(() => {
    const a: RecurringRow[] = [];
    const p: RecurringRow[] = [];
    for (const r of items) {
      if (r.deleted_at) continue;
      (r.active ? a : p).push(r);
    }
    return { active: a, paused: p };
  }, [items]);

  const monthlyExpense = useMemo(
    () =>
      active
        .filter((r) => r.kind === 'expense' && r.currency === currency)
        .reduce((s, r) => s + r.amount_minor, 0),
    [active, currency],
  );
  const monthlyIncome = useMemo(
    () =>
      active
        .filter((r) => r.kind === 'income' && r.currency === currency)
        .reduce((s, r) => s + r.amount_minor, 0),
    [active, currency],
  );

  const findCategory = (id: string) =>
    categories.find((c) => c.id === id);

  const toggleActive = (row: RecurringRow): void => {
    const willBeActive = !row.active;
    const patch: Partial<RecurringRow> = {
      active: willBeActive,
      updated_at: new Date().toISOString(),
    };
    updateDef(row.id, patch);
    enqueueOp({
      type: 'update_recurring',
      payload: { id: row.id, rev: row.rev, patch: patch as Record<string, unknown> },
    });
    // Re-enabling a paused recurring should immediately catch up missed
    // entries — otherwise the user has to wait for tomorrow / a cold load.
    if (willBeActive) {
      kickScheduler(useStore.getState(), enqueueOp);
    }
  };

  const handleSave = (form: RecurringFormPatch): void => {
    const now = new Date().toISOString();
    if (editing) {
      const freqPair = freqPairFromForm(form);
      const patch: Partial<RecurringRow> = {
        title: form.title,
        note: form.note,
        amount_minor: form.amount_minor,
        currency: form.currency,
        kind: form.kind,
        category_id: form.category_id,
        subcategory_id: form.subcategory_id,
        ...freqPair,
        start_date: form.start_date,
        end_date: form.end_date,
        active: form.active,
        updated_at: now,
      } as Partial<RecurringRow>;
      updateDef(editing.id, patch);
      enqueueOp({
        type: 'update_recurring',
        payload: { id: editing.id, rev: editing.rev, patch: patch as Record<string, unknown> },
      });
    } else {
      const fresh = buildFreshRow(form, activeBookId, now);
      addDef(fresh);
      enqueueOp({
        type: 'create_recurring',
        payload: {
          id: fresh.id,
          book_id: fresh.book_id,
          title: fresh.title,
          amount_minor: fresh.amount_minor,
          currency: fresh.currency,
          kind: fresh.kind,
          category_id: fresh.category_id,
          subcategory_id: fresh.subcategory_id,
          note: fresh.note,
          freq: fresh.freq,
          freq_day: fresh.freq_day,
          start_date: fresh.start_date,
          end_date: fresh.end_date,
          last_generated_date: fresh.last_generated_date,
          active: fresh.active,
        },
      });
    }
    setEditing(null);
    setShowNew(false);
    // Run the catch-up scheduler now so a freshly added (or edited)
    // recurring def generates today's tx immediately — without waiting
    // for the next cold load or tab visibility flip. `kickScheduler`
    // resets the per-day dedupe guard so the call lands even if the
    // scheduler already ran earlier in the same session.
    kickScheduler(useStore.getState(), enqueueOp);
  };

  return (
    <div
      style={{
        height: '100%',
        overflow: 'auto',
        background: 'var(--bg-app)',
        paddingBottom: 120,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '60px 20px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="返回設定"
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
                marginLeft: -8,
              }}
            >
              <Icon name="chevL" size={22} stroke="var(--fg-1)" />
            </button>
          )}
          <h1
            style={{
              font: '600 26px var(--font-cjk-display)',
              color: 'var(--fg-1)',
              letterSpacing: '-0.01em',
              margin: 0,
            }}
          >
            固定週期
          </h1>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          aria-label="新增固定週期"
          style={{
            padding: '6px 10px',
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            font: '500 14px var(--font-cjk)',
            color: 'var(--accent)',
          }}
        >
          <Icon name="plus" size={20} stroke="var(--accent)" />
        </button>
      </div>

      {/* Summary */}
      <div style={{ padding: '8px 16px 8px' }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <SummaryCard
            label="月度支出"
            amount={monthlyExpense}
            currency={currency}
            color="var(--fg-1)"
          />
          <SummaryCard
            label="月度收入"
            amount={monthlyIncome}
            currency={currency}
            color="var(--matcha-700)"
            withSign
          />
        </div>
        <div
          style={{
            font: '400 11px var(--font-cjk)',
            color: 'var(--fg-3)',
            marginTop: 10,
            padding: '0 4px',
            lineHeight: 1.5,
          }}
        >
          下次會於各項排定日期自動新增一筆交易；你可以隨時編輯、暫停或刪除。
        </div>
      </div>

      {/* Active list */}
      <Group title={`進行中 · ${active.length}`}>
        {active.length === 0 ? (
          <div
            style={{
              padding: '20px 14px',
              font: '400 13px var(--font-cjk)',
              color: 'var(--fg-3)',
              textAlign: 'center',
            }}
          >
            還沒有任何固定週期。點右上角「+」新增。
          </div>
        ) : (
          active.map((r, i) => (
            <RowItem
              key={r.id}
              row={r}
              isLast={i === active.length - 1}
              category={findCategory(r.category_id)}
              subcategory={findCategory(r.subcategory_id)}
              currency={currency}
              onTap={() => setEditing(r)}
              onToggle={() => toggleActive(r)}
            />
          ))
        )}
      </Group>

      {paused.length > 0 && (
        <Group title={`已暫停 · ${paused.length}`} faded>
          {paused.map((r, i) => (
            <RowItem
              key={r.id}
              row={r}
              isLast={i === paused.length - 1}
              category={findCategory(r.category_id)}
              subcategory={findCategory(r.subcategory_id)}
              currency={currency}
              onTap={() => setEditing(r)}
              onToggle={() => toggleActive(r)}
            />
          ))}
        </Group>
      )}

      {(editing || showNew) && (
        <RecurringEditSheet
          row={editing ?? null}
          defaultCurrency={currency}
          categories={visibleCategories}
          onClose={() => {
            setEditing(null);
            setShowNew(false);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

/** Project the discriminated form into the (freq, freq_day) pair shaped
 *  for the equivalent RecurringRow union variant. */
function freqPairFromForm(
  form: RecurringFormPatch,
): Pick<RecurringRow, 'freq' | 'freq_day'> {
  switch (form.freq) {
    case 'monthly':
      return { freq: 'monthly', freq_day: form.freq_day };
    case 'weekly':
      return { freq: 'weekly', freq_day: form.freq_day };
    case 'daily':
      return { freq: 'daily', freq_day: 0 };
    case 'yearly':
      return { freq: 'yearly', freq_day: 0 };
  }
}

/** Build a fresh `RecurringRow` for create-mode submission. */
function buildFreshRow(
  form: RecurringFormPatch,
  activeBookId: string | null,
  now: string,
): RecurringRow {
  const base = {
    id: genId(),
    book_id: activeBookId ?? '',
    title: form.title,
    amount_minor: form.amount_minor,
    currency: form.currency,
    kind: form.kind,
    category_id: form.category_id,
    subcategory_id: form.subcategory_id,
    note: form.note,
    start_date: form.start_date,
    end_date: form.end_date,
    last_generated_date: '',
    active: form.active,
    rev: 1,
    created_at: now,
    updated_at: now,
    deleted_at: '' as const,
  };
  switch (form.freq) {
    case 'monthly':
      return { ...base, freq: 'monthly', freq_day: form.freq_day };
    case 'weekly':
      return { ...base, freq: 'weekly', freq_day: form.freq_day };
    case 'daily':
      return { ...base, freq: 'daily', freq_day: 0 };
    case 'yearly':
      return { ...base, freq: 'yearly', freq_day: 0 };
  }
}

function Group({
  title,
  faded,
  children,
}: {
  title: string;
  faded?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div style={{ padding: '14px 16px 0' }}>
      <div
        style={{
          font: '600 12px var(--font-cjk)',
          color: 'var(--fg-2)',
          letterSpacing: '0.06em',
          padding: '0 4px 8px',
        }}
      >
        {title}
      </div>
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: 'var(--shadow-1)',
          opacity: faded ? 0.85 : 1,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  amount,
  currency,
  color,
}: {
  label: string;
  amount: number;
  currency: string;
  color: string;
  withSign?: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        flex: 1,
        background: 'var(--bg-surface)',
        borderRadius: 14,
        padding: '12px 14px',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <div
        style={{
          font: '500 10px var(--font-cjk)',
          color: 'var(--fg-3)',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div
        style={{
          font: '600 18px var(--font-num)',
          fontVariantNumeric: 'tabular-nums',
          color,
          marginTop: 2,
        }}
      >
        {formatAmount(amount, currency)}
      </div>
    </div>
  );
}

function RowItem({
  row,
  isLast,
  category,
  subcategory,
  currency,
  onTap,
  onToggle,
}: {
  row: RecurringRow;
  isLast: boolean;
  category?: { color_token: string; icon: string; name: string };
  /** Sub-category row matching `row.subcategory_id` — its icon wins. */
  subcategory?: { icon: string };
  currency: string;
  onTap: () => void;
  onToggle: () => void;
}): JSX.Element {
  const color = category ? `var(${category.color_token})` : 'var(--cat-other)';
  const iconName = displayTxIcon(category, subcategory) as IconName;
  const freqLabel = FREQ_LABELS[row.freq](row.freq_day);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderBottom: isLast ? 0 : '0.5px solid var(--border-1)',
      }}
    >
      <button
        type="button"
        onClick={onTap}
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          background: 'transparent',
          border: 0,
          cursor: 'pointer',
          padding: 0,
          textAlign: 'left',
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 11,
            background: row.active ? color : 'var(--sand-300)',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            opacity: row.active ? 1 : 0.6,
          }}
        >
          <Icon name={iconName} size={18} stroke="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              minWidth: 0,
            }}
          >
            <div
              style={{
                font: '600 14px var(--font-cjk)',
                color: row.active ? 'var(--fg-1)' : 'var(--fg-3)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.title}
            </div>
            {!row.active && (
              <span
                style={{
                  font: '500 9px var(--font-cjk)',
                  color: 'var(--fg-3)',
                  background: 'var(--sand-300)',
                  padding: '1px 6px',
                  borderRadius: 999,
                }}
              >
                已暫停
              </span>
            )}
          </div>
          <div
            style={{
              font: '400 11.5px var(--font-cjk)',
              color: 'var(--fg-3)',
              marginTop: 3,
            }}
          >
            {freqLabel}
            {row.last_generated_date
              ? ` · 上次 ${row.last_generated_date}`
              : ''}
          </div>
        </div>
        <div
          style={{
            font: '600 14px var(--font-num)',
            fontVariantNumeric: 'tabular-nums',
            color: row.active
              ? row.kind === 'income'
                ? 'var(--matcha-700)'
                : 'var(--fg-1)'
              : 'var(--fg-3)',
            flexShrink: 0,
          }}
        >
          {formatAmount(row.amount_minor, row.currency || currency)}
        </div>
      </button>

      {/* Active toggle */}
      <button
        type="button"
        onClick={onToggle}
        aria-label={row.active ? '暫停' : '啟用'}
        aria-pressed={row.active}
        style={{
          width: 42,
          height: 26,
          borderRadius: 13,
          background: row.active ? 'var(--matcha-500)' : 'var(--sand-300)',
          border: 0,
          cursor: 'pointer',
          position: 'relative',
          flexShrink: 0,
          transition: 'background 160ms var(--ease-standard, ease)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 2,
            left: 2,
            width: 22,
            height: 22,
            borderRadius: 11,
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            transform: row.active ? 'translateX(16px)' : 'translateX(0)',
            transition: 'transform 160ms var(--ease-standard, ease)',
          }}
        />
      </button>
    </div>
  );
}
